import { useEssentials } from 'hooks/utils/useEssentials'
import useRefresh from 'hooks/utils/useRefresh'
import { useLocation } from 'react-router-dom'
import { useDirectorAudioSummary } from './useDirectorAudioSummary'
import { AudioPlayerScope } from 'queries/insights/insights'
import { useDirectorAudioTranscript } from './useDirectorAudioTranscript'

interface UseAudioDataResult {
    audio: {
        url?: string | null
        error?: string
        isLoading: boolean
    }
    transcript: {
        data?: string
        error?: string
        isLoading: boolean
    }
}

export const useAudioData = (): UseAudioDataResult => {
    const { params } = useEssentials()
    const urlLocation = useLocation()

    const isStoreLocation = params.get('locationId')?.startsWith('T') ?? false
    const isWeeklyTab = urlLocation.pathname.includes('weekly')
    const {
        data: audioData,
        isLoading: isAudioLoading,
        isError: isAudioError,
        error: audioError,
        refetch,
        remove,
    } = useDirectorAudioSummary(
        isStoreLocation,
        isWeeklyTab ? AudioPlayerScope.LastWeek : AudioPlayerScope.Daily,
    )

    const {
        data: transcriptData,
        isLoading: isTranscriptLoading,
        isError: isTranscriptError,
        error: transcriptError,
    } = useDirectorAudioTranscript(
        isStoreLocation,
        isWeeklyTab ? AudioPlayerScope.LastWeek : AudioPlayerScope.Daily,
    )

    useRefresh(refetch, remove, false)

    // If they are not at a store location or they are viewing a store that doesn't have data
    // show error page with button to set the location to their assigned store
    if (
        !isStoreLocation ||
        (isAudioError && (audioError as Error)?.message.includes('404'))
    ) {
        return {
            audio: {
                url: undefined,
                error: 'Audio not available',
                isLoading: false,
            },
            transcript: {
                data: undefined,
                error: 'Transcript not available',
                isLoading: false,
            },
        }
    }

    if (isAudioError || isTranscriptError) {
        const audioState = isAudioError
            ? {
                  url: undefined,
                  error:
                      (audioError as Error)?.message ??
                      'An error occurred while fetching the audio summary.',
                  isLoading: false,
              }
            : {
                  url: audioData,
                  error: undefined,
                  isLoading: false,
              }

        const transcriptState = isTranscriptError
            ? {
                  data: undefined,
                  error:
                      (transcriptError as Error)?.message ??
                      'An error occurred while fetching the audio transcript.',
                  isLoading: false,
              }
            : {
                  data: transcriptData?.script_text,
                  error: undefined,
                  isLoading: false,
              }

        return {
            audio: audioState,
            transcript: transcriptState,
        }
    }

    if (isAudioLoading || isTranscriptLoading) {
        return {
            audio: {
                url: undefined,
                error: undefined,
                isLoading: true,
            },
            transcript: {
                data: undefined,
                error: undefined,
                isLoading: true,
            },
        }
    }

    return {
        audio: {
            url: audioData,
            error: undefined,
            isLoading: false,
        },
        transcript: {
            data: transcriptData?.script_text,
            error: undefined,
            isLoading: false,
        },
    }
}
